import * as Flux from 'dg-web-shared/lib/Flux';

import * as SettingsState from '../../../common/state/SettingsState';
import { LeftColumn, RightColumn, StaticPage } from './StaticPage';
import { Helpdesk } from './Helpdesk';
import { Address } from './Address';
import { texts as newsletterUnsubscribePageTexts } from '../i18n/NewsletterUnsubscribePageTexts';
import { css } from '@emotion/css';
import {
    RequestMethod,
    useServerFetch,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { RequestStatus } from 'dg-web-shared/lib/ServerRequestStateSlices';
import { Button } from 'semantic-ui-react';
import { CurrentLoginState } from '../../../common/state/CurrentLoginState';
import CurrentLogin = CurrentLoginState.CurrentLogin;

export interface NewsletterUnsubscribePageTexts {
    button: (email: string) => string;
    label: () => string;
    success: () => string;
    failure: () => string;
}

export interface NewsletterUnsubscribeState {
    hash: string | null;
    email: string | null;
    list: string | null;
    valid: boolean;
}

export class StateSlice extends Flux.StateSlice<NewsletterUnsubscribeState> {
    key(): string {
        return 'pages-NewsletterUnsubscribePage';
    }

    getInitialState(): NewsletterUnsubscribeState {
        return {
            hash: null,
            email: null,
            list: null,
            valid: false,
        };
    }

    sideEffects(): null {
        return null;
    }

    update(props: NewsletterUnsubscribeState): void {
        this.set(
            (s: NewsletterUnsubscribeState): NewsletterUnsubscribeState => {
                s.hash = props.hash;
                s.email = props.email;
                s.list = props.list;
                s.valid =
                    props.hash != null &&
                    props.email != null &&
                    props.list != null;
                return s;
            },
        );
    }
}

export const NewsletterUnsubscribePage = () => {
    const { storeState } = Flux.useStore(s => ({
        unsubscribeParams: new StateSlice(s).state,
        settings: new SettingsState.StateSlice(s).state,
    }));

    const isNewsletterPayload = (
        payload: NewsletterUnsubscribeState | CurrentLogin,
    ): payload is NewsletterUnsubscribeState => 'valid' in payload;

    const [writeState, unsubscribe] = useServerWrite<
        NewsletterUnsubscribeState | CurrentLogin,
        object
    >(payload => ({
        url: isNewsletterPayload(payload)
            ? '/ui-api/newsletter/unsubscribe'
            : '/ui-api/customer-account/current-login-v2',
        method: isNewsletterPayload(payload)
            ? RequestMethod.POST
            : RequestMethod.PUT,
    }));

    const [userState] = useServerFetch<CurrentLogin, object>(
        () => ({
            url: '/ui-api/customer-account/current-login-v2',
        }),
        {},
    );

    const texts = newsletterUnsubscribePageTexts[storeState.settings.language];
    const currentLogin =
        userState.status === RequestStatus.SUCCESS ? userState.data : null;

    const canUnsubscribe =
        storeState.unsubscribeParams.valid ||
        userState.status === RequestStatus.SUCCESS;
    const mayUnsubscribe =
        canUnsubscribe || userState.status !== RequestStatus.ERROR;

    const email = storeState.unsubscribeParams.valid
        ? storeState.unsubscribeParams.email!
        : currentLogin?.email
          ? currentLogin.email
          : 'impossible state';

    return (
        <StaticPage>
            <LeftColumn>
                <div className={css({ fontFamily: 'roboto' })}>
                    <h1>{texts.label()}</h1>
                    {!canUnsubscribe && mayUnsubscribe ? (
                        <div>Loading...</div>
                    ) : null}
                    {canUnsubscribe &&
                    (writeState.status === RequestStatus.NEVER_EXECUTED ||
                        writeState.status !== RequestStatus.SUCCESS) ? (
                        <NewsletterUnsubscribeForm
                            email={email}
                            texts={texts}
                            onUnsubscribe={() => {
                                if (storeState.unsubscribeParams.valid) {
                                    unsubscribe(storeState.unsubscribeParams);
                                    return;
                                }
                                if (currentLogin) {
                                    unsubscribe({
                                        ...currentLogin,
                                        newsletterStatus: 'UNSUBSCRIBED_BY_US',
                                    });
                                }
                            }}
                        />
                    ) : null}
                    {writeState.status === RequestStatus.SUCCESS ? (
                        <Message text={texts.success()} />
                    ) : null}
                    {writeState.status === RequestStatus.ERROR ||
                    !mayUnsubscribe ? (
                        <Message text={texts.failure()} />
                    ) : null}
                </div>
            </LeftColumn>
            <RightColumn>
                <Helpdesk />
                <Address />
            </RightColumn>
        </StaticPage>
    );
};

const Message = (p: { text: string }) => (
    <div
        className={css({
            marginBottom: '20px',
        })}
    >
        <label
            className={css({
                marginBottom: '10px',
                fontWeight: 'bold',
            })}
        >
            {p.text}
        </label>
    </div>
);

export const NewsletterUnsubscribeForm = (p: {
    email: string;
    texts: NewsletterUnsubscribePageTexts;
    onUnsubscribe: () => void;
}) => (
    <Button onClick={() => p.onUnsubscribe()}>
        <Button.Content>{p.texts.button(p.email || '')}</Button.Content>
    </Button>
);
